import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck } from "lucide-react";
import { EASE } from "@/lib/lenis";
import { META } from "@/content/meta";
import { ADDONS, CARE_PLANS, PACKAGES } from "@/content/services";
import { usePageMeta } from "@/hooks/usePageMeta";
import { PageHero } from "@/components/site/PageHero";
import { FinalCTA } from "@/components/site/FinalCTA";
import { SectionIntro } from "@/components/site/Bits";
import { PlanCard } from "@/components/services/PlanCard";

const JUMPS = [
  ["websites", "Websites"],
  ["care-plans", "Care plans"],
  ["automation", "Automation"],
];

const Group = ({ id, eyebrow, title, copy, items, kind, cols }) => (
  <section id={id} className="scroll-mt-24 border-t border-white/[0.08] py-20 lg:py-28" data-testid={`pricing-group-${id}`}>
    <div className="container-x">
      <SectionIntro eyebrow={eyebrow} title={title} copy={copy} testId={`pricing-heading-${id}`} />
      <div className={`mt-14 grid gap-6 ${cols}`}>
        {items.map((plan, i) => (
          <PlanCard key={plan.id} plan={plan} kind={kind} index={i} />
        ))}
      </div>
    </div>
  </section>
);

export default function Services() {
  usePageMeta(META.services);
  return (
    <>
      <PageHero
        eyebrow="Services & Pricing"
        lines={["Simple packages.", "Fixed prices."]}
        copy="Pick a starting point and we'll tailor it to your business. Every project gets a clear, fixed quote, so there are no surprises."
        testId="services-hero"
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.65 }}
          className="mt-10 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between"
        >
          <p
            className="inline-flex w-fit items-center gap-3 rounded-full border border-[#2DD4D4]/30 bg-[#2DD4D4]/[0.06] px-4 py-2 text-sm text-white"
            data-testid="fixed-pricing-note"
          >
            <ShieldCheck className="h-4 w-4 shrink-0 text-[#2DD4D4]" aria-hidden="true" />
            Fixed pricing, confirmed before any work begins.
          </p>
          <nav aria-label="Jump to section" className="flex flex-wrap gap-2">
            {JUMPS.map(([id, label]) => (
              <Link
                key={id}
                to={`/services#${id}`}
                className="rounded-full border border-white/10 px-3.5 py-1.5 text-sm text-[#A1A1AA] transition-colors hover:border-white/25 hover:text-white"
                data-testid={`services-jump-${id}`}
              >
                {label}
              </Link>
            ))}
          </nav>
        </motion.div>
      </PageHero>
      <Group
        id="websites"
        eyebrow="Website packages"
        title="Websites"
        copy="Every package starts with a free mockup and includes a site designed for phones first."
        items={PACKAGES}
        kind="Website"
        cols="lg:grid-cols-3"
      />
      <Group
        id="care-plans"
        eyebrow="Monthly care plans"
        title="Care plans"
        copy="Keep your site secure, current, and working, with a local team on call when something needs to change."
        items={CARE_PLANS}
        kind="Monthly care"
        cols="md:grid-cols-2"
      />
      <Group
        id="automation"
        eyebrow="Automation add-ons"
        title="Automation"
        copy="Add these to any website package to catch more calls, collect more reviews, and fill your calendar."
        items={ADDONS}
        kind="Add-on"
        cols="md:grid-cols-2 lg:grid-cols-3"
      />
      <FinalCTA />
    </>
  );
}
